import { Slot } from '@radix-ui/react-slot'
import { cn } from 'lib/utils'
import { ChevronRight } from 'lucide-react'
import * as React from 'react'

interface BreadcrumbsProps {
  children: React.ReactNode
}

export const Breadcrumbs = ({ children }: BreadcrumbsProps) => (
  <nav className="flex items-center space-x-2" aria-label="Breadcrumb">
    {children}
  </nav>
)

interface BreadcrumbListProps {
  children: React.ReactNode
  className?: string
}

export const BreadcrumbList = ({ children, className }: BreadcrumbListProps) => (
  <ol className={cn('flex flex-wrap items-center gap-1.5 break-words text-sm', className)}>
    {children}
  </ol>
)

interface BreadcrumbItemProps {
  children: React.ReactNode
  className?: string
}

export const BreadcrumbItem = ({ children, className }: BreadcrumbItemProps) => (
  <li className={cn('inline-flex items-center gap-1.5', className)}>{children}</li>
)

interface BreadcrumbLinkProps extends React.ComponentPropsWithoutRef<'a'> {
  asChild?: boolean
  href?: string
}

export const BreadcrumbLink = React.forwardRef<HTMLAnchorElement, BreadcrumbLinkProps>(
  ({ asChild = false, className, ...props }, ref) => {
    const Comp = asChild ? Slot : 'a'
    return (
      <Comp
        ref={ref}
        className={cn('transition-colors hover:text-foreground text-muted-foreground', className)}
        {...props}
      />
    )
  },
)
BreadcrumbLink.displayName = 'BreadcrumbLink'

interface BreadcrumbPageProps {
  children: React.ReactNode
  className?: string
}

export const BreadcrumbPage = ({ children, className }: BreadcrumbPageProps) => (
  <span className={cn('font-normal text-primary text-s-400', className)} aria-current="page">
    {children}
  </span>
)

export const BreadcrumbSeparator = ({ className }: { className?: string }) => (
  <li role="presentation" aria-hidden="true" className={cn('text-muted-foreground', className)}>
    <ChevronRight className="h-4 w-4" />
  </li>
)
